import React, { useCallback, useEffect, useState } from "react";
import api from "../../../api";
import { formatOutboundPhoneInput } from "./scheduleUtils";
import "../../Modal/dialog.css";
import "./OutboundCampaign.css";

const PHONE_REGEX = /^\(\d{3}\)\s?\d{3}-\d{4}$/;

const footerStyle = { display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap" };

const TestCampaignModal = ({
    open,
    campaign,
    userId,
    initialVariableNames = [],
    initialVariableValues = {},
    onClose,
    onSuccess,
}) => {
    const [step, setStep] = useState(1);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [variableNames, setVariableNames] = useState([]);
    const [variableValues, setVariableValues] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [updatingVariables, setUpdatingVariables] = useState(false);
    const [error, setError] = useState("");
    const [successPhone, setSuccessPhone] = useState("");
    const [invalidVariableNames, setInvalidVariableNames] = useState(() => new Set());

    const hasVariables = variableNames.length > 0;
    const isBusy = submitting || updatingVariables;

    const getEmptyVariableNames = useCallback(() => {
        return variableNames.filter((name) => !String(variableValues[name] ?? "").trim());
    }, [variableNames, variableValues]);

    const allVariablesFilled = !hasVariables || getEmptyVariableNames().length === 0;
    const isPhoneValid = PHONE_REGEX.test(phoneNumber);

    useEffect(() => {
        if (!open) return;

        setStep(1);
        setPhoneNumber("");
        setSubmitting(false);
        setUpdatingVariables(false);
        setError("");
        setSuccessPhone("");
        setInvalidVariableNames(new Set());

        const names = initialVariableNames || [];
        setVariableNames(names);
        const initial = {};
        names.forEach((name) => {
            initial[name] = initialVariableValues?.[name] ?? "";
        });
        setVariableValues(initial);
    }, [open, initialVariableNames, initialVariableValues]);

    const handleClose = () => {
        if (isBusy) return;
        onClose();
    };

    const validatePhone = () => {
        if (!phoneNumber.trim()) {
            setError("Phone number is required.");
            return false;
        }
        if (!PHONE_REGEX.test(phoneNumber)) {
            setError("Valid phone number required ((555) 123-4567).");
            return false;
        }
        setError("");
        return true;
    };

    const validateVariables = () => {
        const empty = getEmptyVariableNames();
        if (empty.length) {
            setInvalidVariableNames(new Set(empty));
            setError("Please provide values for all variables before continuing.");
            return false;
        }
        setInvalidVariableNames(new Set());
        setError("");
        return true;
    };

    const handleVariableChange = (name, value) => {
        setVariableValues((prev) => ({ ...prev, [name]: value }));
        setInvalidVariableNames((prev) => {
            if (!prev.has(name)) return prev;
            const next = new Set(prev);
            next.delete(name);
            return next;
        });
        if (String(value ?? "").trim()) {
            setError("");
        }
    };

    const handleNext = () => {
        if (!validatePhone()) return;
        if (hasVariables) {
            setInvalidVariableNames(new Set());
            setError("");
            setStep(2);
        } else {
            handleSubmit();
        }
    };

    const handleUpdateVariables = async () => {
        if (!validateVariables()) return;
        if (!campaign?.workspace_id) return;

        setUpdatingVariables(true);
        setError("");

        const variables = {};
        variableNames.forEach((name) => {
            variables[name] = String(variableValues[name] ?? "").trim();
        });

        try {
            const res = await api.botbuilder.saveWorkspaceTestVariables({
                workspace_id: campaign.workspace_id,
                variables,
            });
            if (res && res.status === false) {
                setError(res.message || "Could not save variable values. Please try again.");
            } else {
                setError("");
            }
        } catch {
            setError("Could not save variable values. Please try again.");
        } finally {
            setUpdatingVariables(false);
        }
    };

    const handleSubmit = async () => {
        if (!validatePhone()) return;
        if (hasVariables && !validateVariables()) return;

        setSubmitting(true);
        setError("");

        const payload = {
            user_id: userId,
            phone_number: phoneNumber.replace(/\s/g, ""),
            name: "Test Caller",
        };

        if (hasVariables) {
            const dynamicVariables = {};
            variableNames.forEach((name) => {
                dynamicVariables[name] = String(variableValues[name] ?? "").trim();
            });
            payload.dynamic_variables = dynamicVariables;
        }

        try {
            const response = await api.outbound.testCampaign(campaign.id, payload);
            if (response?.status) {
                setSuccessPhone(response?.data?.phone_number || phoneNumber);
                setStep(3);
                if (typeof onSuccess === "function") {
                    onSuccess(response);
                }
            } else {
                setError(response?.message || "Failed to initiate test call.");
            }
        } catch (err) {
            setError(err?.message || "Failed to initiate test call.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open || !campaign) {
        return null;
    }

    const modalTitle = step === 3 ? null : "Test Campaign";

    const modalMaxWidth = step === 2 ? 700 : 480;

    const renderStep1Body = () => (
        <>
            <p className="test-campaign-modal__intro">
                Enter your phone number to test this campaign. You will receive a call just like your contacts would.
            </p>
            <div className="test-campaign-field">
                <label htmlFor="testCampaignPhone" className="test-campaign-field__label">
                    Phone Number <span className="test-campaign-field__required">*</span>
                </label>
                <div className="test-campaign-phone-input">
                    <div className="test-campaign-phone-input__country">
                        <span className="test-campaign-phone-input__flag" aria-hidden="true">
                            🇺🇸
                        </span>
                        <span className="test-campaign-phone-input__code">+1</span>
                    </div>
                    <input
                        id="testCampaignPhone"
                        type="tel"
                        className="test-campaign-phone-input__number"
                        value={phoneNumber}
                        onChange={(e) => {
                            setPhoneNumber(formatOutboundPhoneInput(e.target.value));
                            setError("");
                        }}
                        placeholder="(555) 123-4567"
                        disabled={submitting}
                        autoComplete="tel"
                    />
                </div>
            </div>
            {hasVariables ? (
                <div className="test-campaign-info-alert">
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>
                        This campaign includes variables. You can provide values for them in the next step.
                    </span>
                </div>
            ) : null}
        </>
    );

    const renderStep2Body = () => (
        <>
            <p className="test-campaign-modal__intro">
                This campaign includes the following variables. Please provide the values you would like to use for testing.
            </p>
            <div className="table-responsive" style={{ marginTop: "0.5rem" }}>
            <table className="table table-bordered table-sm mb-0">
                <thead className="thead-light">
                    <tr>
                        <th scope="col" style={{ width: "38%" }}>
                            Variable Name
                        </th>
                        <th scope="col">Test Value</th>
                    </tr>
                </thead>
                <tbody>
                    {variableNames.map((name) => {
                        const isInvalid = invalidVariableNames.has(name);
                        return (
                            <tr key={name} className={isInvalid ? "table-danger" : undefined}>
                                <td className="align-middle">
                                    <code className="test-call-variables-modal__var-name">{name}</code>
                                </td>
                                <td>
                                    <input
                                        id={`test_campaign_var_${name}`}
                                        type="text"
                                        className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                                        name={`test_var_${name}`}
                                        value={variableValues[name] ?? ""}
                                        onChange={(e) => handleVariableChange(name, e.target.value)}
                                        disabled={isBusy}
                                        placeholder="__________"
                                        autoComplete="off"
                                        aria-invalid={isInvalid}
                                        style={{ borderRadius: "4px" }}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
        </>
    );

    const renderStep3Body = () => (
        <div className="test-campaign-success">
            <div className="test-campaign-success__icon" aria-hidden="true">
                <i className="fas fa-check" />
            </div>
            <h4 className="test-campaign-success__title">Test Initiated!</h4>
            <p className="test-campaign-success__message">
                We are calling {successPhone || phoneNumber} now. You will receive the call shortly.
            </p>
        </div>
    );

    const renderFooter = () => {
        if (step === 3) {
            return (
                <button
                    type="button"
                    className="btn linear-gradient yr-submitbtn btn-sm"
                    onClick={handleClose}
                >
                    <span>Close</span>
                </button>
            );
        }

        if (step === 2) {
            return (
                <>
                    <button
                        type="button"
                        className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        onClick={() => {
                            setStep(1);
                            setError("");
                            setInvalidVariableNames(new Set());
                        }}
                        disabled={isBusy}
                    >
                        <span>Back</span>
                    </button>
                    <button
                        type="button"
                        className="btn linear-gradient yr-submitbtn btn-sm"
                        onClick={handleUpdateVariables}
                        disabled={isBusy || !allVariablesFilled}
                        title={!allVariablesFilled ? "Fill in all variable values to update" : undefined}
                    >
                        <span>{updatingVariables ? "Updating..." : "Update"}</span>
                    </button>
                    <button
                        type="button"
                        className="btn linear-gradient yr-submitbtn btn-sm"
                        onClick={handleSubmit}
                        disabled={isBusy || !allVariablesFilled}
                        title={!allVariablesFilled ? "Fill in all variable values to continue" : undefined}
                    >
                        <span>{submitting ? "Testing..." : "Test Now"}</span>
                    </button>
                </>
            );
        }

        return (
            <>
                <button
                    type="button"
                    className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                    onClick={handleClose}
                    disabled={submitting}
                >
                    <span>Cancel</span>
                </button>
                <button
                    type="button"
                    className="btn linear-gradient yr-submitbtn btn-sm"
                    onClick={handleNext}
                    disabled={submitting || !isPhoneValid}
                    title={!isPhoneValid ? "Enter a valid phone number to continue" : undefined}
                >
                    <span>{submitting ? "Testing..." : hasVariables ? "Next" : "Test Now"}</span>
                </button>
            </>
        );
    };

    return (
        <div
            className="modal completedchats fade show"
            data-backdrop="static"
            tabIndex="-1"
            role="dialog"
            aria-labelledby={step !== 3 ? "testCampaignModalTitle" : undefined}
            aria-hidden="false"
            style={{ display: "block", zIndex: 100050 }}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                role="document"
                style={{ maxWidth: modalMaxWidth }}
            >
                <div className="modal-content test-call-variables-modal">
                    <button
                        type="button"
                        className="close linear-gradient"
                        data-dismiss="modal"
                        aria-label="Close"
                        onClick={handleClose}
                        disabled={isBusy}
                        style={{ zIndex: 100051 }}
                    >
                        X
                    </button>
                    {modalTitle ? (
                        <div className="modal-header test-call-variables-modal__header">
                            <h5
                                className="modal-title test-call-variables-modal__title"
                                id="testCampaignModalTitle"
                            >
                                {modalTitle}
                            </h5>
                        </div>
                    ) : null}
                    <div
                        className={`modal-body parsedatap viewemail test-campaign-modal__body${step === 3 ? " test-campaign-modal__body--success" : ""}`}
                        style={{ paddingTop: modalTitle ? "10px" : "28px" }}
                    >
                        <div style={{ marginLeft: "-5px" }}>
                            {error ? (
                                <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
                                    {error}
                                </div>
                            ) : null}
                            {step === 1 && renderStep1Body()}
                            {step === 2 && renderStep2Body()}
                            {step === 3 && renderStep3Body()}
                        </div>
                    </div>
                    <div className="modal-footer test-call-variables-modal__footer" style={footerStyle}>
                        {renderFooter()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestCampaignModal;
